// src/components/ChatBot/MessageInputComponent.js
import React, { useState, useRef, useEffect } from 'react';
import { MdSend, MdMic, MdStop } from 'react-icons/md';
import { useChat } from './ChatContext';
import { getToken } from "../../rtkq/services/localStorageService";
import { PulseLoader } from 'react-spinners';

const BACKEND_BASE_URL = process.env.REACT_APP_BACKEND_SERVER_URL || "http://127.0.0.1:8000";

export const MessageInputComponent = () => {
  const [inputMessage, setInputMessage] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);

  // Call useChat() once at the top level to get all context values
  const {
    messages,         // Main chat messages state (for userMessageCount)
    setMessages,      // Setter for main chat messages (for optimistic UI and error)
    setTutorMessages, // Setter for tutor messages (for optimistic UI and error)
    mode,
    subType,
    setSubType,
    subtypeOptions,
    handleStreamingResponse, // Function to process the backend stream
    setIsWaitingForResponse, // Function to set loading state
    activeStream,      // Current active stream ('chat' or 'tutor')
  } = useChat();

  // Calculate user message count based on the main 'chat' stream messages
  const userMessageCount = messages.filter(msg => msg.role === 'user').length;

  // Determine if input controls should be disabled (only for 'chat' stream reaching its limit)
  const isSendDisabled = activeStream === 'chat' && userMessageCount >= 10;

  // Refs for media recording
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const recordingTimerRef = useRef(null);
  const recordingTimeoutRef = useRef(null);
  const MAX_RECORDING_TIME = 120; // 2 minutes

  // Define stopRecording here so it can be added to useEffect dependency array if needed
  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop();
      // isRecording state and timer will be reset in the onstop handler
    } else {
      // If stopRecording is called when not actually recording, ensure waiting is false
      setIsWaitingForResponse(false);
    }
  };

  // Effect for recording timer and auto-stop
  useEffect(() => {
    if (isRecording) {
      recordingTimerRef.current = setInterval(() => {
        setRecordingTime(prev => (prev >= MAX_RECORDING_TIME - 1 ? MAX_RECORDING_TIME : prev + 1));
      }, 1000);
      recordingTimeoutRef.current = setTimeout(() => {
        if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
          console.log('Automatic recording stop triggered after timeout');
          stopRecording(); // Call the defined stopRecording function
        }
      }, MAX_RECORDING_TIME * 1000);
    } else {
      clearInterval(recordingTimerRef.current);
      clearTimeout(recordingTimeoutRef.current);
      setRecordingTime(0);
    }
    return () => {
      clearInterval(recordingTimerRef.current);
      clearTimeout(recordingTimeoutRef.current);
    };
  }, [isRecording]); // Removed stopRecording from deps as it's stable now


  const handleSendMessage = async () => {
    // Allow sending if:
    // 1. Input has text AND
    // 2. EITHER it's the tutor stream OR (it's the chat stream AND not disabled)
    const canSendTutor = activeStream === 'tutor' && inputMessage.trim();
    const canSendChat = activeStream === 'chat' && inputMessage.trim() && !isSendDisabled;

    if (!canSendTutor && !canSendChat) {
        return;
    }

    setIsWaitingForResponse(true);
    const messageToSend = inputMessage;
    setInputMessage('');

    // Optimistically add user's message to the correct UI stream
    const userMessageObj = { role: 'user', content: messageToSend, rating: null };
    if (activeStream === 'tutor') {
      setTutorMessages(prev => [...prev, userMessageObj]);
    } else {
      setMessages(prev => [...prev, userMessageObj]);
    }

    try {
      const { access_token } = getToken();
      const response = await fetch(`${BACKEND_BASE_URL}/api/chat/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${access_token}` },
        body: JSON.stringify({
          prompt: messageToSend,
          mode: mode,
          subType: subType,
          stream: activeStream
        }),
      });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const reader = response.body.getReader();
      await handleStreamingResponse(reader);
    } catch (error) {
      console.error('Error sending message:', error);
      const errorMsg = { role: 'bot', content: "Sorry, an error occurred. Please try again." };
      if (activeStream === 'tutor') setTutorMessages(prev => [...prev, errorMsg]);
      else setMessages(prev => [...prev, errorMsg]);
    } finally {
      setIsWaitingForResponse(false);
    }
  };

  const startRecording = async () => {
    if (isSendDisabled && activeStream === 'chat') return; // Prevent recording if chat limit reached

    setIsWaitingForResponse(true);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream);
      audioChunksRef.current = [];
      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data.size > 0) audioChunksRef.current.push(event.data);
      };

      mediaRecorderRef.current.onstop = async () => {
        stream.getTracks().forEach(track => track.stop());
        setIsRecording(false); // Update UI
        setRecordingTime(0);  // Reset timer

        if (audioChunksRef.current.length === 0) {
          console.warn('No audio data recorded');
          setIsWaitingForResponse(false); return;
        }
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        if (audioBlob.size < 1000) {
          console.warn('Audio blob too small');
          setIsWaitingForResponse(false); return;
        }

        const reader = new FileReader();
        reader.onloadend = async () => {
          const base64Audio = reader.result.split(',')[1];
          // Optimistic UI update for transcription can be done here if desired,
          // or wait for backend's 'transcription_only' chunk
          // For now, we wait for the backend to confirm transcription via processJsonChunk
          try {
            const { access_token } = getToken();
            if (!access_token) throw new Error("No access token");
            const response = await fetch(`${BACKEND_BASE_URL}/api/chat/`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${access_token}` },
              body: JSON.stringify({
                audio: base64Audio, mode: mode, subType: subType, stream: activeStream
              }),
            });
            if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
            const responseReader = response.body.getReader();
            await handleStreamingResponse(responseReader);
          } catch (error) {
            console.error('Error sending audio:', error);
            const errorMsg = { role: 'bot', content: `Audio processing failed: ${error.message}` };
            if (activeStream === 'tutor') setTutorMessages(prev => [...prev, errorMsg]);
            else setMessages(prev => [...prev, errorMsg]);
          } finally {
            setIsWaitingForResponse(false);
          }
        };
        reader.onerror = (error) => {
            console.error("FileReader error:", error);
            setIsWaitingForResponse(false);
        };
        reader.readAsDataURL(audioBlob);
      };

      mediaRecorderRef.current.start(1000);
      setIsRecording(true);
      setRecordingTime(0);
    } catch (error) {
      console.error('Microphone access error:', error);
      alert(`Microphone error: ${error.message}. Please check permissions.`);
      setIsRecording(false);
      setIsWaitingForResponse(false);
    }
  };

  return (
    <div className="input-container">
      <textarea
        value={inputMessage}
        onChange={(e) => setInputMessage(e.target.value)}
        onKeyPress={(e) => {
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            // Check if sending is allowed based on current state
            const canSendTutor = activeStream === 'tutor' && inputMessage.trim();
            const canSendChat = activeStream === 'chat' && inputMessage.trim() && !isSendDisabled;
            if (canSendTutor || canSendChat) {
                 handleSendMessage();
            }
          }
        }}
        className="message-textarea"
        placeholder={
            activeStream === 'chat'
              ? (isSendDisabled ? "Message limit reached for this topic." : "Type or record your response...")
              : "Ask the tutor about your English..."
        }
        rows={3}
        disabled={isSendDisabled && activeStream === 'chat'} // Only disable if chat stream and limit reached
        autoComplete="off" autoCorrect="off" autoCapitalize="off" spellCheck="false"
      />

      <div className="controls-container">
        <button
          onClick={handleSendMessage}
          className={`send-button ${(isSendDisabled && activeStream === 'chat') ? 'disabled' : ''}`}
          disabled={(isSendDisabled && activeStream === 'chat') || !inputMessage.trim()}
        >
          <MdSend /> Send
        </button>

        <div className="relative">
          <button
            onClick={isRecording ? stopRecording : startRecording}
            className={`record-button ${
              isRecording
              ? 'record-button-active'
              : (isSendDisabled && activeStream === 'chat')
                  ? 'record-button-disabled'
                  : 'record-button-inactive'
            }`}
            disabled={isSendDisabled && activeStream === 'chat'}
          >
            {isRecording ? <MdStop /> : <MdMic />}
            {isRecording ? 'Stop' : 'Record'}
          </button>
          {isRecording && (
            <span className="recording-timer">
              {Math.floor(recordingTime / 60)}:{(recordingTime % 60).toString().padStart(2, '0')} / {Math.floor(MAX_RECORDING_TIME / 60)}:00
            </span>
          )}
        </div>

        {/* Subtype select - Render only when chat stream is active */}
        {activeStream === 'chat' && (
          <select
            value={subType}
            onChange={(e) => setSubType(e.target.value)}
            className="subtype-select"
            disabled={isSendDisabled} // Disable if chat limit reached
          >
            {/* Ensure subtypeOptions[mode] is an array before mapping */}
            {Array.isArray(subtypeOptions[mode]) && subtypeOptions[mode].map(option => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        )}
        {/* Placeholder or info when tutor stream is active and subtype select is hidden */}
        {activeStream === 'tutor' && (
            <div className="tutor-input-info"></div>
        )}
      </div>

      {/* Show limit alert only when chat stream is active and disabled */}
      {(isSendDisabled && activeStream === 'chat') && (
        <div className="message-limit-alert">
          Message limit reached for this topic. Switch topic/mode or use Tutor.
        </div>
      )}
    </div>
  );
};

export default MessageInputComponent;